/**
 * AsyncSelect — combobox với debounced server search.
 *
 * M4 simple: input → fetch → list. M5 sẽ thêm virtualization + group + create.
 */
import * as React from "react";

import { api } from "@/api/client";
import { Input } from "@/components/ui/input";

interface AsyncSelectProps {
  endpoint: string;
  valueField: string;
  labelField: string;
  minChars: number;
  placeholder?: string;
  value: string | number | null;
  onChange: (val: string | number | null) => void;
}

interface OptionItem {
  [key: string]: unknown;
}

export function AsyncSelect({
  endpoint,
  valueField,
  labelField,
  minChars,
  placeholder,
  value,
  onChange,
}: AsyncSelectProps): JSX.Element {
  const [query, setQuery] = React.useState("");
  const [items, setItems] = React.useState<OptionItem[]>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [selectedLabel, setSelectedLabel] = React.useState<string>("");

  const debouncedQuery = useDebounce(query, 300);

  React.useEffect(() => {
    if (debouncedQuery.length < minChars) {
      setItems([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const res = await api.get<{ items: OptionItem[] }>(endpoint, {
          query: { q: debouncedQuery, limit: 20 },
        });
        if (!cancelled) setItems(res.items);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, endpoint, minChars]);

  return (
    <div className="relative">
      <Input
        placeholder={
          value ? selectedLabel || `(đã chọn #${value})` : (placeholder ?? "Tìm...")
        }
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && items.length > 0 ? (
        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-md border bg-popover shadow-md">
          {items.map((item) => {
            const v = item[valueField] as string | number;
            const lab = String(item[labelField] ?? `#${v}`);
            return (
              <button
                type="button"
                key={String(v)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent"
                onClick={() => {
                  onChange(v);
                  setSelectedLabel(lab);
                  setQuery(lab);
                  setOpen(false);
                }}
              >
                {lab}
              </button>
            );
          })}
        </div>
      ) : null}
      {loading ? (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          ...
        </span>
      ) : null}
    </div>
  );
}

function useDebounce<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}
